import axios from "axios";
import { DataTable } from "simple-datatables";
import { handleRequestError, showAlert } from "../utils/alert";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  reportList: [],
  hasSearched: false,

  summary: {
    total_critical_products: 0,
    total_units_to_buy: 0,
    threshold_used: 5,
  },

  dataTableInstance: null,

  filters: {
    threshold: 5, // Default threshold
  },

  async initCriticalStock() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Must await the table load so errors can bubble up and be evaluated
      await this.loadReportTable();
    } catch (error) {
      // Failsafe in case error bubbles up here (handled mainly inside loadReportTable)
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  getStatusBadge(status) {
    if (status === "Esgotado") {
      return '<span class="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded-md font-bold text-xs uppercase">Esgotado</span>';
    }
    return '<span class="bg-orange-100 text-orange-800 border border-orange-200 px-2 py-0.5 rounded-md font-bold text-xs uppercase">Crítico</span>';
  },

  async loadReportTable() {
    if (this.filters.threshold === "" || this.filters.threshold < 0) {
      return showAlert(
        "Por favor, informe um limite de estoque válido.",
        "warning",
      );
    }

    try {
      const queryParams = new URLSearchParams(this.filters).toString();
      const response = await axios.get(
        `/api/client/reports/critical-stock?${queryParams}`,
      );

      this.reportList = response.data.data;
      this.summary = response.data.summary;
      this.hasSearched = true;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#criticalStockTable tbody");
      if (!tbody) return;

      tbody.innerHTML = this.reportList
        .map((row) => {
          return `
          <tr class="hover:bg-slate-50 border-b border-gray-100">
            <td class="p-3 text-sm font-bold text-slate-800">${row.name}</td>
            <td class="p-3 text-sm font-mono text-center text-slate-700">${row.current_stock}</td>
            <td class="p-3 text-center">${this.getStatusBadge(row.status)}</td>
            <td class="p-3 text-sm font-mono font-bold text-emerald-600 text-center">+ ${row.suggested_repurchase}</td>
          </tr>
        `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#criticalStockTable", {
        searchable: false,
        sortable: true,
        perPageSelect: false,
        labels: {
          placeholder: "Pesquisar produto...",
          searchTitle: "Pesquisar",
          noRows: "Nenhum produto em estado crítico.",
          info: "Mostrando {start} a {end} de {rows} produtos",
        },
      });
    } catch (error) {
      // Intercept the 403 Forbidden specifically and block module access immediately
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        throw error; // Bubble up to stop execution
      }
      handleRequestError(error, "Erro ao carregar relatório de estoque.");
    }
  },

  async applyFilters() {
    try {
      await this.loadReportTable();
    } catch (error) {
      // Catch silently here since the 403 already sets hasAccessModule to false,
      // and other errors are handled by handleRequestError inside loadReportTable.
    }
  },

  exportToCSV() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados para exportar.", "warning");
    let csvContent = "\uFEFFProduto;Estoque Atual;Status;Sugestão de Compra\n";

    this.reportList.forEach((row) => {
      csvContent += `"${row.name}";"${row.current_stock}";"${row.status}";"${row.suggested_repurchase}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `estoque_critico_abaixo_de_${this.filters.threshold}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  async exportToPDF() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados.", "warning");

    try {
      // Request download token securely
      const response = await axios.post(
        "/api/client/reports/request-download-token",
      );
      const downloadToken = response.data.download_token;

      // Prepare filters and append token
      const queryParams = new URLSearchParams(this.filters);
      queryParams.append("dtoken", downloadToken);

      // Open new tab
      window.open(
        `/client/reports/critical-stock/pdf?${queryParams.toString()}`,
        "_blank",
      );
    } catch (error) {
      handleRequestError(
        error,
        "Erro ao solicitar autorização para impressão.",
      );
    }
  },
});
